import React, { useContext, useState } from 'react'
import { getNamedContext, moduleContext } from 'react-contextual'
import { stringUtils } from '../../utils'
import { useToggle } from '../hooks'
import { RouteComponentProps } from 'react-router'

export interface SearchFilterContextValue {
  filter: string | string[] | boolean
  isSearchOpen: boolean
  toggleSearchOpen: () => void
  activeFilterIndex: number
  setActiveFilterIndex: React.Dispatch<React.SetStateAction<number>>
  searchQuery: string
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>
}

interface SearchFilterProviderProps {
  context: React.Context<SearchFilterContextValue>
  location: RouteComponentProps['location']
  children: React.ReactNode
}

const SearchFilterProvider = moduleContext()(({ context, children, location }: SearchFilterProviderProps) => {
  const [isSearchOpen, toggleSearchOpen] = useToggle(false)
  const [activeFilterIndex, setActiveFilterIndex] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const { filter = false } = stringUtils.parseQueryString(location.search)

  const value = {
    filter,
    isSearchOpen,
    toggleSearchOpen,
    activeFilterIndex,
    setActiveFilterIndex,
    searchQuery,
    setSearchQuery,
  }

  return <context.Provider value={value}>{children}</context.Provider>
})

/**
 * Read the nearest SearchFilterProvider from a function component, instead of
 * wrapping it with react-contextual's `subscribe`. moduleContext registers its
 * React context under the provider component, so it can be looked up directly.
 */
export const useSearchFilter = (): SearchFilterContextValue => {
  const context: SearchFilterContextValue | undefined = useContext(getNamedContext(SearchFilterProvider))
  if (context === undefined) {
    throw new Error('useSearchFilter must be used within a SearchFilterProvider')
  }
  return context
}

export default SearchFilterProvider
